import AdjustOutlinedIcon from '@mui/icons-material/AdjustOutlined';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CropFreeOutlinedIcon from '@mui/icons-material/CropFreeOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExposureOutlinedIcon from '@mui/icons-material/ExposureOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FindInPageOutlinedIcon from '@mui/icons-material/FindInPageOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import WorkHistoryOutlinedIcon from '@mui/icons-material/WorkHistoryOutlined';
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getChannelSourceLabel, getSourceChannels, getSourceChannelsQueryKey } from 'api/markerTypes';
import { getUploadList } from 'api/uploads';
import { ActionDialogType, useActiveActionDialogType, useAdditionalParamsDialog } from 'components/ActionDialog';
import { DialogWithCloseButton } from 'components/atoms/Dialog/DialogWithCloseButton';
import { useStudyPlatformVersionGate } from 'components/JobModal/useStudyPlatformVersionGate';
import Loader from 'components/Loader';
import { getDefaultManifestDialogUrlState } from 'components/StudyDashboard/CaseManifest/dialogState';
import { getManifestDialogUrl } from 'components/StudyDashboard/CaseManifest/manifestDialogUrl';
import { PanelChannelSummary } from 'components/StudyDashboard/MultiplexPanels/PanelChannelSummary';
import { buildWorkflowMarkerNamingCasesParams } from 'components/StudyDashboard/ProceduresPage/Actions/BulkMultiplexMarkerNamingDialog/markerNamingDialogParams';
import { BulkMultiplexNormalizationDialog } from 'components/StudyDashboard/ProceduresPage/Actions/BulkMultiplexNormalizationDialog';
import { DefineMultiplexPanelsDialog } from 'components/StudyDashboard/ProceduresPage/Actions/DefineMultiplexPanelsDialog';
import { CellTypingMarkerPositivityDialog } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/CellTypingMarkerPositivity';
import { createPanelConfigurationDraft } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/CellTypingMarkerPositivity/PanelConfigurationEditor';
import {
  CaseImageAnalysisStepControl,
  RunManualPostIngestPipeline,
} from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunManualPostIngestPipeline';
import { RunMultiplexCellSegmentation } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunMultiplexCellSegmentation';
import { RunMultiplexPeakDetection } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunMultiplexPeakDetection';
import { defaultThresholdConfig } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunMultiplexThreshold';
import { StudySlidesEmptyAnimation } from 'components/StudyDashboard/ProceduresPage/StudySlidesEmptyState';
import { useAllPendingSlides } from 'components/StudyDashboard/ProceduresPage/usePendingSlides';
import { getStudyUploadProgress } from 'components/StudyDashboard/StudyUploadsStatus';
import UploadSlidesModal from 'components/StudyDashboard/UploadSlidesModal/UploadSlidesModal';
import { DatasetTab, WorkflowTab } from 'components/StudyDashboard/useStudyDashboardTabState';
import { JobStatus, JobType } from 'interfaces/job';
import { Permission } from 'interfaces/permissionOption';
import { Study } from 'interfaces/study';
import { filter, find, fromPairs, groupBy, map, size, toPairs } from 'lodash';
import { stringify } from 'qs';
import React, { useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useStudyExportController } from 'services/export/useStudyExportController';
import { getWorkflowExportVisibility } from 'services/export/workflowExportSource';
import { encodeQueryParams, JsonParam, StringParam } from 'use-query-params';
import { isPfizerDemoLab } from 'utils/demo';
import { exportNormalizationValuesCsv } from 'utils/exports';
import { useStainTypeOptions } from 'utils/queryHooks/uiConstantsHooks';
import useStudyChannelMarkerTypes from 'utils/queryHooks/useStudyChannelMarkerTypes';
import { useAnnotationAssignments } from 'utils/useAnnotationAssignments';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { getUrlToJobsPage } from 'utils/useNavigationToJobsPage';
import { usePermissions } from 'utils/usePermissions';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import { useMultiplexPanelApproval } from './useMultiplexPanelApproval';
import { useWorkflowFeaturesDirectDownload } from './useWorkflowFeaturesDirectDownload';
import { useWorkflowStepDoneState } from './useWorkflowStepDoneState';
import { WorkflowCellTablesExportDialog } from './WorkflowCellTablesExportDialog';
import { WorkflowExportSplitButton } from './WorkflowExportSplitButton';
import { WorkflowFeaturesExportDialog } from './WorkflowFeaturesExportDialog';
import { WorkflowStepCollapsedSummary, WorkflowStepDoneControls } from './WorkflowStepDoneControls';

import {
  ActionFormDirtyProvider,
  createActionFormCloseHandler,
  useActionFormDirtyStore,
} from 'components/ActionDialog/actionFormDirtyState';
import { useConfirmation } from 'components/modals/ConfirmationContext';
import { JobStatusChip } from '../../Pages/Jobs/JobStatusChip';
import {
  getAnalysisAreasDatasetUrl,
  getUndoneIncludeExcludeAnnotations,
  isAnalysisAreaAssignment,
} from './analysisAreas';
import { AnalysisAreasDialog } from './AnalysisAreasDialog';
import { AnalysisAreasReadinessNotice } from './AnalysisAreasReadinessNotice';
import { CaseOrganizationNotice } from './CaseOrganizationNotice';
import {
  hiddenCaseOrganizationSlides,
  studyHasHeOrIhcSlides,
  withoutDismissedSlides,
} from './caseOrganizationSlideDismissal';
import { getCellSegmentationReviewUrl, getRnaSpotDetectionReviewUrl } from './cellSegmentationReviewUrl';
import { getStudyWorkflowConfiguration } from './studyWorkflowConfiguration';
import {
  CASE_ORGANIZATION_WORKFLOW_LOADING_MESSAGE,
  getCaseOrganizationWorkflowMetrics,
  getStudyWorkflowImageAnalysisSlideScopes,
  getStudyWorkflowInventory,
} from './studyWorkflowInventory';
import {
  getImageAnalysisTaskStatuses,
  getRnaSpotDetectionTaskStatus,
  ImageAnalysisTaskStatus,
} from './studyWorkflowJobStatus';
import { getLatestWorkflowStepApproval, getStudyWorkflowSteps, getWorkflowApproverName } from './studyWorkflowModel';
import { useCaseOrganizationSlideDismissal } from './useCaseOrganizationSlideDismissal';
import { useCompleteStudyProcedures } from './useCompleteStudyProcedures';
import { useImageAnalysisStatus } from './useImageAnalysisStatus';
import { WorkflowStepMetrics } from './WorkflowStepMetrics';

const workflowStepIcons: Record<string, React.ReactElement> = {
  'dataset-preparation': <CloudUploadOutlinedIcon />,
  'case-organization': <FolderOpenOutlinedIcon />,
  'case-image-analysis': <LayersOutlinedIcon />,
  'marker-assignments': <LabelOutlinedIcon />,
  'marker-normalization': <TuneOutlinedIcon />,
  'analysis-areas': <CropFreeOutlinedIcon />,
  'marker-positivity-and-cell-rules': <ExposureOutlinedIcon />,
  'rna-probes': <AdjustOutlinedIcon />,
  'review-and-publication': <FindInPageOutlinedIcon />,
};

const WorkflowJobStatusChips = ({ taskStatus }: { taskStatus: ImageAnalysisTaskStatus }) => (
  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
    {taskStatus.completedCount ? (
      <JobStatusChip
        status={JobStatus.Completed}
        label={`${taskStatus.completedCount} of ${taskStatus.totalCount} completed`}
      />
    ) : null}
    {taskStatus.runningCount ? (
      <JobStatusChip status={JobStatus.Running} label={`${taskStatus.runningCount} running`} />
    ) : null}
    {taskStatus.pendingCount ? (
      <JobStatusChip status={JobStatus.Pending} label={`${taskStatus.pendingCount} pending`} />
    ) : null}
    {taskStatus.failedCount ? (
      <JobStatusChip status={JobStatus.Failed} label={`${taskStatus.failedCount} failed`} />
    ) : null}
    {taskStatus.cancelledCount ? (
      <JobStatusChip status={JobStatus.Cancelled} label={`${taskStatus.cancelledCount} cancelled`} />
    ) : null}
    {taskStatus.notStartedCount ? (
      <Chip size="small" variant="outlined" label={`${taskStatus.notStartedCount} not started`} />
    ) : null}
  </Stack>
);

interface Props {
  study?: Study;
  isMultiplexStudy: boolean;
  // The study's export statistics are still being read; the results step cannot tell what is exportable yet.
  isLoadingExportAvailability?: boolean;
  hasExportAvailabilityError?: boolean;
}

export const StudyWorkflowMonitor: React.FC<Props> = ({
  study,
  isMultiplexStudy,
  isLoadingExportAvailability = false,
  hasExportAvailabilityError = false,
}) => {
  const { labId } = useCurrentLabId();
  const { hasPermission } = usePermissions();
  const [, setCurrentActionItemType] = useActiveActionDialogType();
  const [, setAdditionalParams] = useAdditionalParamsDialog();
  const [isWorkflowMarkerNamingOpen, setIsWorkflowMarkerNamingOpen] = useState(false);
  const [isWorkflowUploadSlidesOpen, setIsWorkflowUploadSlidesOpen] = useState(false);
  const confirmedEmptyDatasetStudyIdRef = useRef<string | null>(null);
  const resolvedInitialInventoryStudyIdRef = useRef<string | null>(null);
  const [isWorkflowNormalizationOpen, setIsWorkflowNormalizationOpen] = useState(false);
  const normalizationDirtyStore = useActionFormDirtyStore();
  const confirmation = useConfirmation();
  const closeWorkflowNormalization = createActionFormCloseHandler(
    normalizationDirtyStore.getDirtyState,
    (options) =>
      confirmation({
        ...options,
        title: 'Discard changes',
        confirmButtonProps: { title: 'Discard changes', variant: 'contained', color: 'warning' },
        cancelButtonProps: { color: 'inherit' },
      }),
    () => setIsWorkflowNormalizationOpen(false)
  );
  const [isWorkflowSpotDetectionOpen, setIsWorkflowSpotDetectionOpen] = useState(false);
  const [isWorkflowInitialImageAnalysisOpen, setIsWorkflowInitialImageAnalysisOpen] = useState(false);
  const [isWorkflowCellSegmentationOpen, setIsWorkflowCellSegmentationOpen] = useState(false);
  const [isAnalysisAreasOpen, setIsAnalysisAreasOpen] = useState(false);
  const [isWorkflowCellTypingOpen, setIsWorkflowCellTypingOpen] = useState(false);
  const [isFeaturesExportDetailOpen, setIsFeaturesExportDetailOpen] = useState(false);
  const { downloadFeatures, featuresExportUi } = useWorkflowFeaturesDirectDownload(study);
  const [cellTablesExportMode, setCellTablesExportMode] = useState<'direct' | 'detailed' | null>(null);
  const studyId = study?.id || '';
  const normalizationExport = useStudyExportController();
  const { stainTypeIdToDisplayName } = useStainTypeIdToDisplayName(studyId);
  const { isLoading: isCheckingPlatformVersion, platformVersionWarning } = useStudyPlatformVersionGate(studyId, {
    enabled: isWorkflowSpotDetectionOpen || isWorkflowInitialImageAnalysisOpen || isWorkflowCellSegmentationOpen,
  });
  const renderWorkflowJobForm = (form: React.ReactNode) =>
    isCheckingPlatformVersion ? (
      <Skeleton variant="rectangular" width="100%" height={600} aria-label="Checking Platform Version" />
    ) : (
      <>
        {platformVersionWarning}
        {form}
      </>
    );
  const loadMarkerAssignmentInventory =
    isMultiplexStudy || getStudyWorkflowConfiguration(study?.settings?.workflow?.modules).rnaProbes;
  const {
    data: uploadList,
    isLoading: isLoadingUploads,
    isError: isUploadListError,
  } = useQuery({
    queryKey: ['studyUploads', studyId, labId],
    queryFn: () => getUploadList({ studyId, labId }),
    enabled: Boolean(studyId),
    refetchInterval: 120000,
  });
  const uploadProgress = useMemo(() => getStudyUploadProgress(uploadList), [uploadList]);
  const {
    procedures,
    isLoading: isLoadingProcedures,
    isError: isProceduresError,
  } = useCompleteStudyProcedures(studyId, Boolean(studyId));
  const {
    allPendingSlide,
    canQueryPendingSlides: canReadPendingSlideInventory,
    isPendingSlidesLoading,
    pendingSlidesError,
  } = useAllPendingSlides({
    enabled: Boolean(studyId),
    scope: 'study',
    studyId,
    includeHidden: true,
  });
  const hasStudySlideRecords =
    (study?.slidesCount ?? 0) > 0 ||
    procedures.some((procedure) => (procedure.slides?.length ?? 0) > 0) ||
    (allPendingSlide?.length ?? 0) > 0;
  const shouldLoadMarkerAssignmentInventory = loadMarkerAssignmentInventory && hasStudySlideRecords;
  const { stainTypeOptions, isLoadingStainTypeOptions } = useStainTypeOptions({
    enabled: Boolean(studyId) && hasStudySlideRecords,
  });
  const {
    channelMarkersResponse,
    isCheckingStudyChannels,
    isLoadingStainTypeOptions: isLoadingMarkerNames,
    markerTypes,
  } = useStudyChannelMarkerTypes(studyId, {
    enabled: Boolean(studyId) && shouldLoadMarkerAssignmentInventory,
  });
  const sourceChannelsInventoryQuery = useQuery({
    queryKey: getSourceChannelsQueryKey({ labId, filters: { studyId } }),
    queryFn: ({ signal }) => getSourceChannels({ labId, filters: { studyId } }, signal),
    enabled: Boolean(studyId) && shouldLoadMarkerAssignmentInventory,
  });
  const {
    dismissedSlideIds,
    hideSlide,
    restoreSlide,
    savingSlideId: savingDismissedSlideId,
    error: dismissalError,
  } = useCaseOrganizationSlideDismissal(study);
  const visiblePendingSlides = useMemo(
    () => withoutDismissedSlides(allPendingSlide, dismissedSlideIds),
    [allPendingSlide, dismissedSlideIds]
  );
  const hiddenPendingSlides = useMemo(
    () => hiddenCaseOrganizationSlides(allPendingSlide, dismissedSlideIds),
    [allPendingSlide, dismissedSlideIds]
  );
  const unassignedSourceChannels = filter(
    sourceChannelsInventoryQuery.data?.channels,
    (channel) => size(channel.assignedMarkerTypes) === 0
  );
  const studyInventory = useMemo(
    () => getStudyWorkflowInventory(procedures, visiblePendingSlides),
    [procedures, visiblePendingSlides]
  );
  const imageAnalysisSlideScopes = useMemo(() => getStudyWorkflowImageAnalysisSlideScopes(procedures), [procedures]);
  const analysisAreaAssignments = useAnnotationAssignments(studyId, false, true, !studyId);
  const activeAnalysisAreaAssignments = (analysisAreaAssignments.data?.annotationAssignments || []).filter(
    (assignment) => isAnalysisAreaAssignment(assignment) && !assignment.archived
  );
  const analysisAreaAssignment =
    activeAnalysisAreaAssignments.length === 1 ? activeAnalysisAreaAssignments[0] : undefined;
  const analysisAreaSlides = useMemo(
    () =>
      imageAnalysisSlideScopes.multiplexSlideIds.map((slideId) => {
        const owningCase = procedures.find((procedure) => (procedure.slides || []).some((item) => item.id === slideId));
        const slide = owningCase?.slides?.find((item) => item.id === slideId);
        return {
          id: slideId,
          label: slide?.originalFileName || slide?.labInternalId || slideId,
          caseId: owningCase?.id,
        };
      }),
    [imageAnalysisSlideScopes.multiplexSlideIds, procedures]
  );
  const panelSummaryStains = useMemo(
    () =>
      map(stainTypeOptions, (stain) => ({
        ...stain,
        displayName: find(markerTypes, { id: stain.id })?.displayName || stain.displayName,
      })),
    [markerTypes, stainTypeOptions]
  );
  const canRunSpotDetection = hasPermission(Permission.ExecuteMultiplexPeakDetection);
  const hasLoadedSlideInventory =
    !isLoadingProcedures && !isPendingSlidesLoading && (!hasStudySlideRecords || !isLoadingStainTypeOptions);
  const hasLoadedCaseImages = !isLoadingProcedures;
  const hasLoadedMarkerInventory =
    !shouldLoadMarkerAssignmentInventory ||
    (!isCheckingStudyChannels && !isLoadingMarkerNames && Boolean(channelMarkersResponse));
  const hasLoadedMarkerAssignmentPreview =
    shouldLoadMarkerAssignmentInventory && hasLoadedMarkerInventory && !sourceChannelsInventoryQuery.isLoading;
  const markerAssignmentSlideCount = Math.max(
    (channelMarkersResponse?.totalSlidesCount || 0) - size(channelMarkersResponse?.slidesWithMissingChannelAssignments),
    0
  );
  const missingMarkerAssignmentCount = size(channelMarkersResponse?.slidesWithMissingChannelAssignments);
  const missingStainingModalityCount = Math.max(
    studyInventory.slideRecordCount - studyInventory.slidesWithStainingMetadataCount,
    0
  );
  const needsMarkerNamingAction =
    shouldLoadMarkerAssignmentInventory && hasLoadedMarkerInventory && missingMarkerAssignmentCount > 0;
  const canEditManifest = hasPermission(Permission.ApplyCaseManifest);
  const canEditMarkerNames = hasPermission(Permission.EditQCMarkers);
  const canApproveMarkerNames = hasPermission(Permission.EditStudyNameAndDescription);
  const canViewJobs = hasPermission(Permission.ViewJobs);
  const canDownloadCellsTable = hasPermission(Permission.DownloadCellsTable);
  const canViewUnpublishedResults = hasPermission(Permission.ViewUnpublishedResults);
  const canExportFeatures = getWorkflowExportVisibility({
    kind: 'features',
    study,
    canViewUnpublishedResults,
    canDownloadCellsTable,
  });
  const canExportCellTables = getWorkflowExportVisibility({
    kind: 'cellTables',
    study,
    canViewUnpublishedResults,
    canDownloadCellsTable,
  });
  const canSetNormalizationValues = hasPermission(Permission.ExecuteMultiplexNormalization);
  const canRunInitialImageAnalysis = hasPermission(Permission.ExecuteManualPostIngestPipeline);
  const canRunCellSegmentation = hasPermission(Permission.RunMultiplexCellSegmentation);
  const latestMarkerAssignmentsApproval = useMemo(
    () => getLatestWorkflowStepApproval(study?.settings?.workflowStepApprovals, 'marker-assignments'),
    [study?.settings?.workflowStepApprovals]
  );
  const panelApproval = useMultiplexPanelApproval(
    study,
    labId,
    canEditMarkerNames && (hasStudySlideRecords || Boolean(study?.settings?.multiplexPanels?.length))
  );
  const isMarkerAssignmentsApproved = panelApproval.isApproved;
  const {
    data: imageAnalysisStatus,
    isLoading: isLoadingImageAnalysisStatus,
    isError: isImageAnalysisStatusError,
  } = useImageAnalysisStatus({
    studyId,
    slideIds: imageAnalysisSlideScopes.allSlideIds,
    labId,
    enabled: hasLoadedCaseImages && canViewJobs && imageAnalysisSlideScopes.allSlideIds.length > 0,
  });
  const imageAnalysisTaskStatuses = useMemo(
    () => getImageAnalysisTaskStatuses(imageAnalysisSlideScopes, imageAnalysisStatus?.statuses || []),
    [imageAnalysisSlideScopes, imageAnalysisStatus?.statuses]
  );
  const cellSegmentationReviewUrl = useMemo(
    () =>
      getCellSegmentationReviewUrl({
        studyId,
        labId,
        procedures,
        statuses: imageAnalysisStatus?.statuses || [],
      }),
    [imageAnalysisStatus?.statuses, labId, procedures, studyId]
  );
  const rnaSpotDetectionTaskStatus = useMemo(
    () =>
      getRnaSpotDetectionTaskStatus(imageAnalysisSlideScopes.multiplexSlideIds, imageAnalysisStatus?.statuses || []),
    [imageAnalysisSlideScopes.multiplexSlideIds, imageAnalysisStatus?.statuses]
  );
  const rnaSpotDetectionReviewUrl = useMemo(
    () =>
      getRnaSpotDetectionReviewUrl({
        studyId,
        labId,
        procedures,
        statuses: imageAnalysisStatus?.statuses || [],
      }),
    [imageAnalysisStatus?.statuses, labId, procedures, studyId]
  );
  const hasInitialImageAnalysisStatus = Boolean(imageAnalysisStatus);
  const isInitialImageAnalysisStatusLoad = isLoadingImageAnalysisStatus && !hasInitialImageAnalysisStatus;
  const showImageAnalysisStatusError = isImageAnalysisStatusError && !hasInitialImageAnalysisStatus;
  const manifestDialogUrl = studyId
    ? `${getManifestDialogUrl({
        studyId,
        labId,
        manifestState: { ...getDefaultManifestDialogUrlState(), reviewUnassignedSlidesWhenAvailable: true },
        manifestSurface: null,
      })}&studyDashboardTab=${encodeURIComponent(WorkflowTab)}`
    : undefined;
  const workflowMarkerNamingCasesParams = useMemo(
    () =>
      buildWorkflowMarkerNamingCasesParams({
        studyId,
        labId,
        slideIds: [],
      }),
    [labId, studyId]
  );
  const openWorkflowMarkerNaming = () => {
    setAdditionalParams(undefined);
    // The dataset action dialog is URL-backed. Clear it so this workflow action cannot reopen that host.
    setCurrentActionItemType(null as unknown as ActionDialogType);
    setIsWorkflowMarkerNamingOpen(true);
  };
  const workflowNormalizationCasesParams = useMemo(
    () => ({
      studyId,
      labId,
      filters: { studyId },
      slidesMode: true,
    }),
    [labId, studyId]
  );
  const datasetTabUrl = useMemo(() => {
    if (!studyId) {
      return undefined;
    }
    const encodedQuery = encodeQueryParams(
      {
        filters: JsonParam,
        labId: StringParam,
        studyDashboardTab: StringParam,
      },
      {
        filters: { studyId },
        labId,
        studyDashboardTab: DatasetTab,
      }
    );
    return `/procedures?${stringify(encodedQuery)}`;
  }, [labId, studyId]);
  const openWorkflowNormalization = () => {
    setAdditionalParams(undefined);
    setCurrentActionItemType(null as unknown as ActionDialogType);
    setIsWorkflowNormalizationOpen(true);
  };
  const ingestJobsUrl = getUrlToJobsPage({
    labId,
    jobsFilters: {
      studyId,
      types: [JobType.Ingest, JobType.BatchIngest],
    },
  });
  const imageAnalysisJobsUrl = getUrlToJobsPage({
    labId,
    jobsFilters: {
      studyId,
      includePipelines: true,
      types: [
        JobType.BrightfieldTissueSegmentationMask,
        ...(isMultiplexStudy
          ? [JobType.MultiplexTissueSegmentationMask, JobType.MultiplexNormalization, JobType.MultiplexCellSegmentation]
          : []),
      ],
    },
  });
  const spotDetectionJobsUrl = getUrlToJobsPage({
    labId,
    jobsFilters: {
      studyId,
      includePipelines: true,
      types: [JobType.MultiplexPeakDetection],
    },
  });
  const workflowMultiplexCaseSlidesCasesParams = useMemo(
    () => ({
      studyId,
      labId,
      filters: { studyId },
      slidesMode: true,
      slideIdsToInclude: imageAnalysisSlideScopes.multiplexSlideIds,
    }),
    [imageAnalysisSlideScopes.multiplexSlideIds, labId, studyId]
  );
  const openWorkflowSpotDetection = () => {
    setAdditionalParams(undefined);
    setCurrentActionItemType(null as unknown as ActionDialogType);
    setIsWorkflowSpotDetectionOpen(true);
  };
  // Manual runs from the workflow cover case slides only; unassigned slides are analyzed automatically after ingest.
  const workflowInitialImageAnalysisCasesParams = useMemo(
    () => ({
      studyId,
      labId,
      filters: { studyId },
      slidesMode: true,
      slideIdsToInclude: imageAnalysisSlideScopes.allSlideIds,
    }),
    [imageAnalysisSlideScopes.allSlideIds, labId, studyId]
  );
  const openWorkflowInitialImageAnalysis = () => {
    setAdditionalParams(undefined);
    setCurrentActionItemType(null as unknown as ActionDialogType);
    setIsWorkflowInitialImageAnalysisOpen(true);
  };
  const openWorkflowCellSegmentation = () => {
    setAdditionalParams(undefined);
    setCurrentActionItemType(null as unknown as ActionDialogType);
    setIsWorkflowCellSegmentationOpen(true);
  };

  const isDatasetPreparationEmpty =
    Boolean(studyId) &&
    Boolean(uploadList) &&
    !isLoadingUploads &&
    !isUploadListError &&
    !isLoadingProcedures &&
    !isPendingSlidesLoading &&
    !isProceduresError &&
    // Without read access the hook returns an empty list, which is not proof that no slides are pending.
    canReadPendingSlideInventory &&
    !pendingSlidesError &&
    uploadProgress.uploadedSlideCount === 0 &&
    uploadList?.all?.length === 0 &&
    uploadList?.slides?.pending?.length === 0 &&
    uploadList?.slides?.processed?.length === 0 &&
    uploadList?.slides?.failed?.length === 0 &&
    studyInventory.slideRecordCount === 0 &&
    !allPendingSlide?.length &&
    !(study?.slidesCount > 0) &&
    !panelApproval.hasSavedPanels &&
    !getLatestWorkflowStepApproval(study?.settings?.workflowStepApprovals, 'dataset-preparation');
  const datasetPreparationHasContent =
    uploadProgress.uploadedSlideCount > 0 ||
    (uploadList?.all?.length ?? 0) > 0 ||
    (uploadList?.slides?.pending?.length ?? 0) > 0 ||
    (uploadList?.slides?.processed?.length ?? 0) > 0 ||
    (uploadList?.slides?.failed?.length ?? 0) > 0 ||
    studyInventory.slideRecordCount > 0 ||
    Boolean(allPendingSlide?.length) ||
    (study?.slidesCount ?? 0) > 0 ||
    panelApproval.hasSavedPanels ||
    Boolean(getLatestWorkflowStepApproval(study?.settings?.workflowStepApprovals, 'dataset-preparation'));
  // Pending-slide reads report isFetching as loading. That must not replace a confirmed empty
  // study with the populated dataset step while the same empty inventory refreshes.
  if (studyId && isDatasetPreparationEmpty) {
    confirmedEmptyDatasetStudyIdRef.current = studyId;
  }
  if (!studyId || studyId !== confirmedEmptyDatasetStudyIdRef.current || datasetPreparationHasContent) {
    confirmedEmptyDatasetStudyIdRef.current = null;
  }
  const showEmptyDatasetPreparation = confirmedEmptyDatasetStudyIdRef.current === studyId;
  const steps = useMemo(
    () =>
      getStudyWorkflowSteps({
        isMultiplexStudy,
        modules: study?.settings?.workflow?.modules,
        previewConfiguredModules: showEmptyDatasetPreparation,
      }),
    [isMultiplexStudy, showEmptyDatasetPreparation, study?.settings?.workflow?.modules]
  );

  // Each step must show exactly one loading indicator, covering every query that step's content depends on.
  const getStepLoadingState = (stepId: string) => {
    if (stepId === 'dataset-preparation') {
      return (
        isLoadingUploads ||
        (!showEmptyDatasetPreparation && !hasLoadedSlideInventory) ||
        (!showEmptyDatasetPreparation && shouldLoadMarkerAssignmentInventory && !hasLoadedMarkerAssignmentPreview)
      );
    }
    if (stepId === 'case-organization') {
      return !hasLoadedSlideInventory;
    }
    if (stepId === 'marker-assignments') {
      return shouldLoadMarkerAssignmentInventory && (!hasLoadedMarkerInventory || panelApproval.isLoading);
    }
    return isLoadingUploads;
  };

  const getStepLoadingMessage = (stepId: string) => {
    const messages: Record<string, string> = {
      'dataset-preparation': 'Loading slide ingestion and case assignments…',
      'case-organization': CASE_ORGANIZATION_WORKFLOW_LOADING_MESSAGE,
      'marker-assignments': 'Loading panel information across the cohort…',
    };
    return messages[stepId] || 'Loading study workflow information…';
  };

  const getStepMetrics = (stepId: string) => {
    if (stepId !== 'dataset-preparation' && !hasStudySlideRecords) return [];
    if (showEmptyDatasetPreparation && stepId !== 'dataset-preparation') return [];

    if (stepId === 'dataset-preparation') {
      if (showEmptyDatasetPreparation || (!hasStudySlideRecords && uploadProgress.uploadedSlideCount === 0)) return [];
      return [
        { label: 'Uploaded slides', value: uploadProgress.uploadedSlideCount },
        { label: 'In ingest jobs', value: uploadProgress.activeIngestCount },
        { label: 'Ingested', value: uploadProgress.ingestedSlideCount },
      ];
    }

    if (stepId === 'case-organization') {
      return getCaseOrganizationWorkflowMetrics(studyInventory, procedures.length);
    }

    if (stepId === 'marker-assignments' && shouldLoadMarkerAssignmentInventory) {
      return [
        { label: 'Slides assigned', value: markerAssignmentSlideCount },
        { label: 'Slides missing assignment', value: missingMarkerAssignmentCount },
      ];
    }

    return [];
  };

  const getStepAction = (stepId: string) => {
    if (showEmptyDatasetPreparation && stepId !== 'dataset-preparation') return null;

    if (stepId === 'dataset-preparation') {
      if (showEmptyDatasetPreparation) {
        if (isPfizerDemoLab(labId)) return null;
        return (
          <Tooltip title={study?.analysisStartTime ? 'Slides cannot be added after analysis has started.' : ''}>
            <span>
              <Button
                onClick={() => setIsWorkflowUploadSlidesOpen(true)}
                variant="contained"
                color="primary"
                startIcon={<CloudUploadOutlinedIcon />}
                disabled={!study?.name || Boolean(study.analysisStartTime)}
                sx={{ minWidth: 'max-content', whiteSpace: 'nowrap' }}
              >
                Add Slides
              </Button>
            </span>
          </Tooltip>
        );
      }
      return (
        <Button
          component={RouterLink}
          to={ingestJobsUrl}
          variant="outlined"
          color="secondary"
          startIcon={<WorkHistoryOutlinedIcon />}
          sx={{ minWidth: 'max-content', whiteSpace: 'nowrap' }}
        >
          View ingest jobs
        </Button>
      );
    }

    if (stepId === 'case-organization' && canEditManifest && manifestDialogUrl) {
      return (
        <Button
          component={RouterLink}
          to={manifestDialogUrl}
          variant="outlined"
          color="secondary"
          startIcon={<PlaylistAddCheckOutlinedIcon />}
          sx={{ minWidth: 'max-content', whiteSpace: 'nowrap' }}
        >
          Assign slides to cases
        </Button>
      );
    }

    if (stepId === 'case-image-analysis') {
      return (
        <Stack spacing={1} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
          {canRunInitialImageAnalysis && studyId ? (
            <Button
              onClick={openWorkflowInitialImageAnalysis}
              variant="outlined"
              color="secondary"
              startIcon={<AnalyticsOutlinedIcon />}
              disabled={!imageAnalysisSlideScopes.allSlideIds.length}
              sx={{ minWidth: 'max-content', whiteSpace: 'nowrap' }}
            >
              Run initial image analysis
            </Button>
          ) : null}
          {canViewJobs ? (
            <Button
              component={RouterLink}
              to={imageAnalysisJobsUrl}
              variant="outlined"
              color="secondary"
              startIcon={<WorkHistoryOutlinedIcon />}
              sx={{ minWidth: 'max-content', whiteSpace: 'nowrap' }}
            >
              View analysis jobs
            </Button>
          ) : null}
        </Stack>
      );
    }

    if (stepId === 'rna-probes') {
      return (
        <Stack spacing={1} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
          {canRunSpotDetection ? (
            <Button
              onClick={openWorkflowSpotDetection}
              variant="outlined"
              color="secondary"
              startIcon={<AdjustOutlinedIcon />}
              disabled={!imageAnalysisSlideScopes.multiplexSlideIds.length}
              sx={{ minWidth: 'max-content', whiteSpace: 'nowrap' }}
            >
              Run RNA probes spot detection
            </Button>
          ) : null}
          {canViewJobs ? (
            <Button
              component={RouterLink}
              to={spotDetectionJobsUrl}
              variant="outlined"
              color="secondary"
              startIcon={<WorkHistoryOutlinedIcon />}
              sx={{ minWidth: 'max-content', whiteSpace: 'nowrap' }}
            >
              View spot detection jobs
            </Button>
          ) : null}
        </Stack>
      );
    }

    if (stepId === 'marker-normalization') {
      if (!studyId || !datasetTabUrl) {
        return null;
      }
      return (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
          {canSetNormalizationValues ? (
            <Button
              onClick={openWorkflowNormalization}
              variant="outlined"
              color="secondary"
              startIcon={<TuneOutlinedIcon />}
              sx={{ minWidth: 'max-content', whiteSpace: 'nowrap' }}
            >
              Set normalization values
            </Button>
          ) : null}
          {study.allowExport ? (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<FileDownloadOutlinedIcon />}
              disabled={normalizationExport.isExportRunning || isLoadingStainTypeOptions || !labId}
              onClick={() =>
                void normalizationExport.startExport({
                  title: 'Normalization values export',
                  canCancel: true,
                  run: async (signal) => {
                    const count = await exportNormalizationValuesCsv({
                      casesParams: { labId, filters: { studyId } },
                      getMarkerLabel: stainTypeIdToDisplayName,
                      fileNamePrefix: `studyId-${studyId}-study-normalization`,
                      signal,
                    });
                    if (!count) throw new Error('No slides with recorded channels were found in this scope.');
                  },
                })
              }
            >
              Export normalization CSV
            </Button>
          ) : null}
          {canRunCellSegmentation ? (
            <Button
              onClick={openWorkflowCellSegmentation}
              variant="outlined"
              color="secondary"
              startIcon={<LayersOutlinedIcon />}
              disabled={!imageAnalysisSlideScopes.multiplexSlideIds.length}
              sx={{ minWidth: 'max-content', whiteSpace: 'nowrap' }}
            >
              Re-run cell segmentation
            </Button>
          ) : null}
          <Button
            component={RouterLink}
            to={datasetTabUrl}
            variant="outlined"
            color="secondary"
            onClick={() => {
              setAdditionalParams(undefined);
              setCurrentActionItemType(null as unknown as ActionDialogType);
            }}
            sx={{ minWidth: 'max-content', whiteSpace: 'nowrap' }}
          >
            Go to dataset
          </Button>
        </Stack>
      );
    }

    if (stepId === 'marker-assignments') {
      if (!studyId || !(canEditMarkerNames || canApproveMarkerNames)) {
        return null;
      }
      // Opens the marker-naming dialog on this tab with the study's multiplex slides.
      // Do not link into the dataset procedures query or its action dialog.
      return (
        <Button
          onClick={openWorkflowMarkerNaming}
          variant="outlined"
          color="secondary"
          startIcon={<LabelOutlinedIcon />}
          sx={{ minWidth: 'max-content', whiteSpace: 'nowrap' }}
        >
          Define multiplex panels
        </Button>
      );
    }

    if (stepId === 'analysis-areas' && studyId) {
      const exploreDatasetUrl = analysisAreaAssignment
        ? getAnalysisAreasDatasetUrl({
            studyId,
            labId,
            slideIds: analysisAreaSlides.map((slide) => slide.id),
            annotationAssignmentId: analysisAreaAssignment.annotationAssignmentId,
          })
        : undefined;

      return (
        <Stack spacing={1} alignItems="flex-end">
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<CropFreeOutlinedIcon />}
            disabled={!hasLoadedCaseImages}
            onClick={() => setIsAnalysisAreasOpen(true)}
            sx={{ minWidth: 'max-content', whiteSpace: 'nowrap' }}
          >
            {activeAnalysisAreaAssignments.length ? 'Review analysis areas' : 'Add analysis areas'}
          </Button>
          {exploreDatasetUrl ? (
            <Button
              component={RouterLink}
              to={exploreDatasetUrl}
              variant="outlined"
              color="secondary"
              sx={{ minWidth: 'max-content', whiteSpace: 'nowrap' }}
            >
              Explore dataset and annotate areas
            </Button>
          ) : null}
          {analysisAreaAssignment && getUndoneIncludeExcludeAnnotations(analysisAreaAssignment).length ? (
            <AnalysisAreasReadinessNotice
              assignment={analysisAreaAssignment}
              studyId={studyId}
              labId={labId}
              doneLabel="Mark as done"
            />
          ) : null}
        </Stack>
      );
    }

    if (stepId === 'marker-positivity-and-cell-rules' && studyId && canEditMarkerNames) {
      return (
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<ExposureOutlinedIcon />}
          disabled={!hasLoadedCaseImages || !imageAnalysisSlideScopes.multiplexSlideIds.length}
          onClick={() => {
            setAdditionalParams(undefined);
            setCurrentActionItemType(null as unknown as ActionDialogType);
            setIsWorkflowCellTypingOpen(true);
          }}
          sx={{ minWidth: 'max-content', whiteSpace: 'nowrap' }}
        >
          Cell typing &amp; marker positivity
        </Button>
      );
    }

    if (stepId === 'review-and-publication' && studyId && datasetTabUrl) {
      let exportContent: React.ReactNode;
      if (isLoadingExportAvailability) {
        exportContent = <Loader inline message="Checking available results…" />;
      } else if (hasExportAvailabilityError) {
        exportContent = (
          <Typography variant="body2" color="text.secondary" textAlign="right">
            Available results could not be checked.
          </Typography>
        );
      } else if (!canExportFeatures && !canExportCellTables) {
        const noExportNote = !study?.allowExport
          ? 'Exports are disabled for this study.'
          : !canViewUnpublishedResults
          ? 'No published results to export yet.'
          : 'No results to export yet.';
        exportContent = (
          <Typography variant="body2" color="text.secondary" textAlign="right">
            {noExportNote}
          </Typography>
        );
      } else {
        exportContent = (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
            {canExportFeatures ? (
              <WorkflowExportSplitButton
                label="Export features"
                onDownload={downloadFeatures}
                onDownloadSpecificVersions={() => setIsFeaturesExportDetailOpen(true)}
              />
            ) : null}
            {canExportCellTables ? (
              <WorkflowExportSplitButton
                label="Export cell tables"
                onDownload={() => setCellTablesExportMode('direct')}
                onDownloadSpecificVersions={() => setCellTablesExportMode('detailed')}
              />
            ) : null}
          </Stack>
        );
      }
      return (
        <Stack spacing={1} alignItems="flex-end">
          <Button
            component={RouterLink}
            to={datasetTabUrl}
            variant="outlined"
            color="secondary"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => {
              setAdditionalParams(undefined);
              setCurrentActionItemType(null as unknown as ActionDialogType);
            }}
            sx={{ minWidth: 'max-content', whiteSpace: 'nowrap' }}
          >
            View results
          </Button>
          {exportContent}
        </Stack>
      );
    }

    return null;
  };

  const {
    completions: workflowStepCompletions,
    completionError,
    savingStepId,
    savingAction,
    expandedDoneStepIds,
    setExpandedDoneStepIds,
    markStepDone,
    reopenStep,
  } = useWorkflowStepDoneState({
    study,
    studyId,
    steps,
    isLoadingUploads,
    isUploadListError,
    hasLoadedSlideInventory,
    hasLoadedCaseImages,
    isProceduresError,
    uploadProgress,
    studyInventory,
    missingStainingModalityCount,
    caseCount: procedures.length,
    hasInitialImageAnalysisStatus,
    showImageAnalysisStatusError,
    canViewJobs,
    allSlideCount: imageAnalysisSlideScopes.allSlideIds.length,
    multiplexSlideCount: imageAnalysisSlideScopes.multiplexSlideIds.length,
    imageAnalysisTaskStatuses,
    panelApproval,
    rnaSpotDetectionTaskStatus,
  });
  const caseImageAnalysisCompletion = workflowStepCompletions['case-image-analysis'];
  const caseImageAnalysisStepLabel =
    find(steps, (step) => step.id === 'case-image-analysis')?.label || 'Case initial image analysis';
  const caseImageAnalysisStepControl: CaseImageAnalysisStepControl = {
    stepLabel: caseImageAnalysisStepLabel,
    isDone: Boolean(caseImageAnalysisCompletion?.isDone),
    canMarkDone: Boolean(
      caseImageAnalysisCompletion?.canMarkDone &&
        caseImageAnalysisCompletion.summary &&
        caseImageAnalysisCompletion.digest
    ),
    onMarkDone: async () =>
      caseImageAnalysisCompletion?.summary && caseImageAnalysisCompletion.digest
        ? markStepDone(
            'case-image-analysis',
            caseImageAnalysisStepLabel,
            caseImageAnalysisCompletion.summary,
            caseImageAnalysisCompletion.digest
          )
        : 'failed',
  };

  if (resolvedInitialInventoryStudyIdRef.current !== studyId) {
    resolvedInitialInventoryStudyIdRef.current = null;
  }
  const isInitialInventoryLoading =
    isLoadingUploads ||
    !hasLoadedSlideInventory ||
    panelApproval.isLoading ||
    (shouldLoadMarkerAssignmentInventory &&
      (isCheckingStudyChannels || isLoadingMarkerNames || sourceChannelsInventoryQuery.isLoading));
  if (studyId && !isInitialInventoryLoading) {
    resolvedInitialInventoryStudyIdRef.current = studyId;
  }
  if (resolvedInitialInventoryStudyIdRef.current !== studyId) {
    return <Loader inline message="Loading slide ingestion and case assignments…" />;
  }

  return (
    <>
      <Timeline
        position="right"
        aria-label="study workflow steps"
        sx={{
          m: 0,
          width: '100%',
          px: 0,
          py: 2,
          maxWidth: 'lg',
          '& .MuiTimelineItem-root:before': {
            flex: 0,
            p: 0,
          },
        }}
      >
        {map(steps, (step, index) => {
          const isMultiplexStep = step.category === 'multiplex';
          const isStepBlocked = !hasStudySlideRecords && step.id !== 'dataset-preparation';
          const completion = workflowStepCompletions[step.id];
          const isCollapsed = !isStepBlocked && Boolean(completion?.isDone) && !expandedDoneStepIds[step.id];
          const showStepBody = !isCollapsed;
          const showDoneActions = Boolean(
            !isStepBlocked &&
              !getStepLoadingState(step.id) &&
              showStepBody &&
              completion &&
              !(step.id === 'dataset-preparation' && showEmptyDatasetPreparation) &&
              completion.mode !== 'none' &&
              completion.mode !== 'evidence' &&
              (completion.canMarkDone || completion.showReopen)
          );
          const stepMetrics = getStepMetrics(step.id);
          const stepAction = isStepBlocked ? null : getStepAction(step.id);
          const imageAnalysisTaskStatusGroups = toPairs(
            groupBy(
              filter(imageAnalysisTaskStatuses, ({ totalCount }) => totalCount > 0),
              'modalityLabel'
            )
          );

          return (
            <TimelineItem
              key={step.id}
              aria-disabled={isStepBlocked}
              sx={{
                animation: 'studyWorkflowStepEnter 420ms ease-out both',
                animationDelay: `${Math.min(index, 8) * 70}ms`,
                '@keyframes studyWorkflowStepEnter': {
                  from: { opacity: 0, transform: 'translateY(16px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
                '@media (prefers-reduced-motion: reduce)': {
                  animation: 'none',
                },
              }}
            >
              <TimelineSeparator>
                <TimelineDot
                  data-testid={`workflow-step-icon-${step.id}`}
                  color={
                    isStepBlocked ? 'grey' : completion?.isDone ? 'success' : isMultiplexStep ? 'secondary' : 'grey'
                  }
                  variant={isStepBlocked ? 'outlined' : completion?.isDone || isMultiplexStep ? 'filled' : 'outlined'}
                  sx={{
                    m: 0,
                    width: 64,
                    height: 64,
                    display: 'grid',
                    placeItems: 'center',
                    opacity: isStepBlocked ? 0.62 : 1,
                    '& .MuiSvgIcon-root': { fontSize: 38 },
                  }}
                >
                  {workflowStepIcons[step.id]}
                </TimelineDot>
                {index < steps.length - 1 ? <TimelineConnector sx={{ minHeight: 48 }} /> : null}
              </TimelineSeparator>

              <TimelineContent
                sx={{
                  minWidth: 0,
                  containerType: 'inline-size',
                  px: { xs: 2, md: 3 },
                  pt: 0.25,
                  pb: index < steps.length - 1 ? 3 : 1,
                }}
              >
                <Box
                  sx={(theme) => ({
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr)',
                    gap: { xs: 2, md: 3 },
                    alignItems: 'start',
                    ...(!isStepBlocked && {
                      [`@container (min-width: ${theme.breakpoints.values.md}px)`]: {
                        gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
                      },
                    }),
                  })}
                >
                  <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="caption" color="text.secondary">
                        Step {index + 1}
                      </Typography>
                      {isMultiplexStep ? (
                        <Chip size="small" variant="outlined" color="secondary" label="Multiplex" />
                      ) : null}
                      {!isStepBlocked && completion?.isDone ? (
                        <Chip size="small" variant="outlined" color="success" label="Done" />
                      ) : null}
                      {!isStepBlocked && completion?.isDone ? (
                        <IconButton
                          size="small"
                          aria-label={isCollapsed ? `Expand ${step.label}` : `Collapse ${step.label}`}
                          onClick={() => setExpandedDoneStepIds((current) => ({ ...current, [step.id]: isCollapsed }))}
                        >
                          {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                        </IconButton>
                      ) : null}
                    </Stack>
                    <Typography variant="h6" color={isStepBlocked ? 'text.secondary' : 'text.primary'}>
                      {step.label}
                    </Typography>
                    {isCollapsed && completion ? <WorkflowStepCollapsedSummary completion={completion} /> : null}
                    {showStepBody && step.id === 'dataset-preparation' && showEmptyDatasetPreparation ? (
                      <StudySlidesEmptyAnimation compact />
                    ) : null}
                    {showStepBody ? (
                      <Typography variant="body2" color="text.secondary">
                        {step.id === 'dataset-preparation' && showEmptyDatasetPreparation
                          ? 'Add slides to start preparing your dataset.'
                          : step.description}
                      </Typography>
                    ) : null}
                    {showStepBody && stepMetrics.length ? (
                      getStepLoadingState(step.id) ? (
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <CircularProgress size={18} aria-label={getStepLoadingMessage(step.id)} />
                          <Typography variant="caption" color="text.secondary">
                            {getStepLoadingMessage(step.id)}
                          </Typography>
                        </Stack>
                      ) : (
                        <WorkflowStepMetrics metrics={stepMetrics} />
                      )
                    ) : null}

                    {showStepBody && step.id === 'dataset-preparation' && !showEmptyDatasetPreparation ? (
                      <Stack spacing={1.25} sx={{ pt: 0.5 }}>
                        {hasLoadedSlideInventory ? (
                          <Stack spacing={0.65}>
                            <Typography variant="caption" color="text.secondary">
                              Slide metadata · {studyInventory.slidesWithStainingMetadataCount}/
                              {studyInventory.slideRecordCount} with staining modality
                            </Typography>
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                              {map(studyInventory.modalityCounts, ({ label, count }) => (
                                <Chip
                                  key={label}
                                  size="small"
                                  label={`${label} · ${count} ${count === 1 ? 'slide' : 'slides'}`}
                                />
                              ))}
                            </Stack>
                          </Stack>
                        ) : null}

                        {isMultiplexStudy && hasLoadedMarkerAssignmentPreview ? (
                          <Stack spacing={0.65} data-testid="dataset-preparation-marker-preview">
                            <Typography variant="caption" color="text.secondary">
                              Marker assignments · {markerAssignmentSlideCount}/
                              {channelMarkersResponse?.totalSlidesCount || 0} multiplex slides assigned
                            </Typography>
                            <PanelChannelSummary
                              panels={panelApproval.data?.panels || []}
                              stains={panelSummaryStains}
                              labId={labId}
                            />
                            {unassignedSourceChannels.length > 0 ? (
                              <Stack spacing={0.5}>
                                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                  Not yet assigned (shown by source channel name)
                                </Typography>
                                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                  {map(unassignedSourceChannels, (channel) => (
                                    <Chip
                                      key={channel.channelId}
                                      size="small"
                                      variant="outlined"
                                      color="warning"
                                      label={getChannelSourceLabel(channel)}
                                    />
                                  ))}
                                </Stack>
                              </Stack>
                            ) : null}
                          </Stack>
                        ) : null}
                      </Stack>
                    ) : null}

                    {showStepBody && !isStepBlocked && step.id === 'case-organization' && hasLoadedSlideInventory ? (
                      <Stack spacing={1.25} sx={{ pt: 0.5 }}>
                        {studyInventory.caseCompositionCounts.length ? (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Case compositions
                            </Typography>
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.65 }}>
                              {map(studyInventory.caseCompositionCounts, ({ label, count }) => (
                                <Chip
                                  key={label}
                                  size="small"
                                  label={`${label} · ${count} ${count === 1 ? 'case' : 'cases'}`}
                                />
                              ))}
                            </Stack>
                          </Box>
                        ) : null}

                        <CaseOrganizationNotice
                          unassignedSlides={visiblePendingSlides}
                          hiddenSlides={hiddenPendingSlides}
                          missingStainingModalityCount={missingStainingModalityCount}
                          showBrightfieldStainInfo={studyHasHeOrIhcSlides(
                            studyInventory.modalityCounts.map(({ label }) => label)
                          )}
                          canHide={canApproveMarkerNames}
                          savingSlideId={savingDismissedSlideId}
                          error={dismissalError}
                          onHide={(slideId) => {
                            void hideSlide(slideId);
                          }}
                          onRestore={(slideId) => {
                            void restoreSlide(slideId);
                          }}
                        />
                      </Stack>
                    ) : null}

                    {showStepBody && !isStepBlocked && step.id === 'case-image-analysis' && hasLoadedCaseImages ? (
                      <Stack spacing={1.25} sx={{ pt: 0.5 }} aria-label="Case initial image analysis job status">
                        <Typography variant="caption" color="text.secondary">
                          Job status by modality and slide
                        </Typography>
                        {!canViewJobs ? (
                          <Typography variant="body2" color="text.secondary">
                            Job viewing permission is required.
                          </Typography>
                        ) : imageAnalysisSlideScopes.allSlideIds.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No slides with a staining modality in this study.
                          </Typography>
                        ) : isInitialImageAnalysisStatusLoad ? (
                          <CircularProgress size={18} aria-label="Loading case initial image analysis job status" />
                        ) : showImageAnalysisStatusError ? (
                          <Alert severity="error">Case initial image analysis job status could not be loaded.</Alert>
                        ) : (
                          <TableContainer>
                            <Table size="small" aria-label="Image analysis jobs by modality">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ pl: 0, width: 190 }}>Modality</TableCell>
                                  <TableCell sx={{ width: 190 }}>Analysis job</TableCell>
                                  <TableCell>Status</TableCell>
                                  <TableCell align="right" sx={{ width: 120 }}>
                                    Review
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {map(imageAnalysisTaskStatusGroups, ([modalityLabel, taskStatuses]) =>
                                  map(taskStatuses, (taskStatus, taskIndex) => (
                                    <TableRow
                                      key={taskStatus.id}
                                      data-testid={`image-analysis-task-status-${taskStatus.id}`}
                                    >
                                      {taskIndex === 0 ? (
                                        <TableCell rowSpan={taskStatuses.length} sx={{ pl: 0, verticalAlign: 'top' }}>
                                          <Typography variant="subtitle2">{modalityLabel}</Typography>
                                        </TableCell>
                                      ) : null}
                                      <TableCell>
                                        <Typography variant="body2">{taskStatus.label}</Typography>
                                      </TableCell>
                                      <TableCell>
                                        <WorkflowJobStatusChips taskStatus={taskStatus} />
                                      </TableCell>
                                      <TableCell align="right">
                                        {taskStatus.id === 'cell-segmentation' && cellSegmentationReviewUrl ? (
                                          <Button
                                            component={RouterLink}
                                            to={cellSegmentationReviewUrl}
                                            size="small"
                                            variant="outlined"
                                            color="secondary"
                                            startIcon={<VisibilityOutlinedIcon />}
                                            sx={{ minWidth: 'max-content', whiteSpace: 'nowrap' }}
                                          >
                                            Review
                                          </Button>
                                        ) : null}
                                      </TableCell>
                                    </TableRow>
                                  ))
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </Stack>
                    ) : null}

                    {showStepBody && !isStepBlocked && step.id === 'rna-probes' && hasLoadedCaseImages ? (
                      <Stack spacing={1.25} sx={{ pt: 0.5 }} aria-label="RNA probes spot detection job status">
                        <Typography variant="caption" color="text.secondary">
                          Job status by multiplex slide
                        </Typography>
                        {!canViewJobs ? (
                          <Typography variant="body2" color="text.secondary">
                            Job viewing permission is required.
                          </Typography>
                        ) : imageAnalysisSlideScopes.multiplexSlideIds.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No multiplex slides in this study.
                          </Typography>
                        ) : isInitialImageAnalysisStatusLoad ? (
                          <CircularProgress size={18} aria-label="Loading RNA probes spot detection job status" />
                        ) : showImageAnalysisStatusError ? (
                          <Alert severity="error">RNA probes spot detection job status could not be loaded.</Alert>
                        ) : (
                          <TableContainer>
                            <Table size="small" aria-label="RNA probes spot detection jobs">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ pl: 0, width: 190 }}>Modality</TableCell>
                                  <TableCell sx={{ width: 240 }}>Analysis job</TableCell>
                                  <TableCell>Status</TableCell>
                                  <TableCell align="right" sx={{ width: 120 }}>
                                    Review
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                <TableRow data-testid="rna-spot-detection-task-status">
                                  <TableCell sx={{ pl: 0 }}>
                                    <Typography variant="subtitle2">
                                      {rnaSpotDetectionTaskStatus.modalityLabel}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">{rnaSpotDetectionTaskStatus.label}</Typography>
                                  </TableCell>
                                  <TableCell>
                                    <WorkflowJobStatusChips taskStatus={rnaSpotDetectionTaskStatus} />
                                  </TableCell>
                                  <TableCell align="right">
                                    {rnaSpotDetectionReviewUrl ? (
                                      <Button
                                        component={RouterLink}
                                        to={rnaSpotDetectionReviewUrl}
                                        size="small"
                                        variant="outlined"
                                        color="secondary"
                                        startIcon={<VisibilityOutlinedIcon />}
                                        sx={{ minWidth: 'max-content', whiteSpace: 'nowrap' }}
                                      >
                                        Review
                                      </Button>
                                    ) : null}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </Stack>
                    ) : null}

                    {showStepBody && !isStepBlocked && step.id === 'marker-assignments' && hasLoadedMarkerInventory ? (
                      <Stack spacing={1.25} sx={{ pt: 0.5 }} aria-label="Marker assignments status">
                        {panelApproval.data?.panels?.length ? (
                          <Stack spacing={0.65}>
                            <Typography variant="caption" color="text.secondary">
                              Marker assignments · {markerAssignmentSlideCount}/
                              {channelMarkersResponse?.totalSlidesCount || 0} multiplex slides assigned
                            </Typography>
                            <PanelChannelSummary
                              panels={panelApproval.data.panels}
                              stains={panelSummaryStains}
                              labId={labId}
                            />
                          </Stack>
                        ) : null}

                        {needsMarkerNamingAction ? (
                          <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={1.5}
                            alignItems={{ xs: 'flex-start', md: 'center' }}
                            justifyContent="space-between"
                            sx={{ borderLeft: 2, borderColor: 'warning.main', pl: 1.5 }}
                          >
                            <Stack spacing={0.35} sx={{ maxWidth: 560 }}>
                              <Typography variant="subtitle2">Channels need assignment</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {missingMarkerAssignmentCount} multiplex{' '}
                                {missingMarkerAssignmentCount === 1 ? 'slide has' : 'slides have'} unassigned source
                                channels. Match each source channel from the slide metadata to the correct stain or
                                marker so downstream marker analysis can interpret the panel consistently.
                              </Typography>
                            </Stack>
                            {!canEditMarkerNames ? (
                              <Typography variant="caption" color="text.secondary">
                                Marker editing permission is required.
                              </Typography>
                            ) : null}
                          </Stack>
                        ) : panelApproval.hasSavedPanels ? (
                          <Alert
                            severity={
                              panelApproval.isError ? 'error' : isMarkerAssignmentsApproved ? 'success' : 'info'
                            }
                          >
                            {panelApproval.isError
                              ? 'Panel approvals could not be verified.'
                              : `${panelApproval.data?.approvedSlidesCount || 0} of ${
                                  panelApproval.data?.totalSlidesCount || 0
                                } multiplex slides approved through panels.`}
                          </Alert>
                        ) : isMarkerAssignmentsApproved && latestMarkerAssignmentsApproval ? (
                          <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={1.5}
                            alignItems={{ xs: 'flex-start', md: 'center' }}
                            sx={{ borderLeft: 2, borderColor: 'success.main', pl: 1.5 }}
                          >
                            <TaskAltOutlinedIcon color="success" fontSize="small" />
                            <Typography variant="body2" color="text.secondary">
                              {`Approved on ${new Date(
                                latestMarkerAssignmentsApproval.approvedAt
                              ).toLocaleString()} by ${getWorkflowApproverName(
                                latestMarkerAssignmentsApproval,
                                study?.workflowStepApproverNames
                              )}`}
                            </Typography>
                          </Stack>
                        ) : latestMarkerAssignmentsApproval ? (
                          <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={1.5}
                            alignItems={{ xs: 'flex-start', md: 'center' }}
                            justifyContent="space-between"
                            sx={{ borderLeft: 2, borderColor: 'warning.main', pl: 1.5 }}
                          >
                            <Stack spacing={0.35} sx={{ maxWidth: 560 }}>
                              <Typography variant="subtitle2">Approval undone</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {`Undone on ${new Date(
                                  latestMarkerAssignmentsApproval.approvedAt
                                ).toLocaleString()} by ${getWorkflowApproverName(
                                  latestMarkerAssignmentsApproval,
                                  study?.workflowStepApproverNames
                                )}. Approve again before running marker-dependent analyses.`}
                              </Typography>
                            </Stack>
                            {!canApproveMarkerNames ? (
                              <Typography variant="caption" color="text.secondary">
                                Study editing permission is required to approve.
                              </Typography>
                            ) : null}
                          </Stack>
                        ) : (
                          <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={1.5}
                            alignItems={{ xs: 'flex-start', md: 'center' }}
                            justifyContent="space-between"
                            sx={{ borderLeft: 2, borderColor: 'warning.main', pl: 1.5 }}
                          >
                            <Stack spacing={0.35} sx={{ maxWidth: 560 }}>
                              <Typography variant="subtitle2">Approval required</Typography>
                              <Typography variant="body2" color="text.secondary">
                                Marker assignments must be approved before running marker-dependent analyses.
                              </Typography>
                            </Stack>
                            {!canApproveMarkerNames ? (
                              <Typography variant="caption" color="text.secondary">
                                Study editing permission is required to approve.
                              </Typography>
                            ) : null}
                          </Stack>
                        )}
                      </Stack>
                    ) : null}
                    {showStepBody &&
                    !isStepBlocked &&
                    !(step.id === 'dataset-preparation' && showEmptyDatasetPreparation) ? (
                      <WorkflowStepDoneControls
                        part="notices"
                        stepLabel={step.label}
                        completion={completion}
                        error={completionError?.stepId === step.id ? completionError.message : undefined}
                        isSaving={savingStepId === step.id}
                        onMarkDone={() => undefined}
                        onReopen={() => undefined}
                      />
                    ) : null}
                  </Stack>

                  {!isStepBlocked ? (
                    <Box
                      data-testid={`workflow-step-action-${step.id}`}
                      sx={{
                        minWidth: 0,
                        maxWidth: '100%',
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        justifySelf: 'end',
                        alignItems: 'flex-start',
                      }}
                    >
                      {showStepBody && (stepAction || showDoneActions) ? (
                        <Stack
                          spacing={1}
                          alignItems="flex-end"
                          sx={{ minWidth: 0, width: '100%', '& > *': { maxWidth: '100%' } }}
                        >
                          {stepAction}
                          {showDoneActions ? (
                            <WorkflowStepDoneControls
                              part="actions"
                              stepLabel={step.label}
                              completion={completion}
                              error={completionError?.stepId === step.id ? completionError.message : undefined}
                              isSaving={savingStepId === step.id}
                              savingAction={savingStepId === step.id ? savingAction : undefined}
                              onMarkDone={() => {
                                if (completion?.summary && completion.digest) {
                                  void markStepDone(step.id, step.label, completion.summary, completion.digest);
                                }
                              }}
                              onReopen={() => {
                                void reopenStep(step.id);
                              }}
                            />
                          ) : null}
                        </Stack>
                      ) : null}
                    </Box>
                  ) : null}
                </Box>
                <Divider data-testid={`workflow-step-divider-${step.id}`} sx={{ mt: 3, borderColor: 'divider' }} />
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
      {isWorkflowUploadSlidesOpen ? (
        <UploadSlidesModal isOpen studyId={studyId} onClose={() => setIsWorkflowUploadSlidesOpen(false)} />
      ) : null}
      {normalizationExport.exportProgressDialog}
      {featuresExportUi}
      {isFeaturesExportDetailOpen ? (
        <WorkflowFeaturesExportDialog study={study} onClose={() => setIsFeaturesExportDetailOpen(false)} />
      ) : null}
      {cellTablesExportMode ? (
        <WorkflowCellTablesExportDialog
          study={study}
          autoDownload={cellTablesExportMode === 'direct'}
          onClose={() => setCellTablesExportMode(null)}
        />
      ) : null}
      {isAnalysisAreasOpen ? (
        <AnalysisAreasDialog
          studyId={studyId}
          labId={labId}
          slides={analysisAreaSlides}
          onClose={() => setIsAnalysisAreasOpen(false)}
        />
      ) : null}
      {isWorkflowCellTypingOpen ? (
        <CellTypingMarkerPositivityDialog
          key={`${studyId}-${labId}`}
          studyId={studyId}
          labId={labId}
          thresholdFlowDefaults={defaultThresholdConfig}
          panels={panelApproval.data?.panels || []}
          selectedSlideIds={imageAnalysisSlideScopes.multiplexSlideIds}
          configurationSources={[
            {
              id: 'count-rule-defaults',
              kind: 'platform',
              label: 'Default count and rule settings',
              panelDrafts: fromPairs(
                map(panelApproval.data?.panels || [], (panel) => [panel.panelId, createPanelConfigurationDraft(panel)])
              ),
            },
          ]}
          isLoading={panelApproval.isLoading}
          error={panelApproval.isError ? 'Panel configuration could not be loaded.' : undefined}
          onClose={() => setIsWorkflowCellTypingOpen(false)}
        />
      ) : null}
      {isWorkflowMarkerNamingOpen ? (
        <DefineMultiplexPanelsDialog
          casesParams={{ ...workflowMarkerNamingCasesParams, slideIdsToInclude: undefined }}
          onClose={() => setIsWorkflowMarkerNamingOpen(false)}
        />
      ) : null}
      {isWorkflowNormalizationOpen ? (
        <ActionFormDirtyProvider value={normalizationDirtyStore.contextValue}>
          <DialogWithCloseButton header="Set normalization values" onClose={closeWorkflowNormalization}>
            <BulkMultiplexNormalizationDialog
              casesParams={workflowNormalizationCasesParams}
              queryStudyMultiplexSlides
              onClose={closeWorkflowNormalization}
            />
          </DialogWithCloseButton>
        </ActionFormDirtyProvider>
      ) : null}
      {isWorkflowSpotDetectionOpen ? (
        isCheckingPlatformVersion ? (
          <DialogWithCloseButton
            header="RNA probes spot detection"
            onClose={() => setIsWorkflowSpotDetectionOpen(false)}
          >
            <Skeleton variant="rectangular" width="100%" height={600} aria-label="Checking Platform Version" />
          </DialogWithCloseButton>
        ) : (
          <RunMultiplexPeakDetection
            casesParams={workflowMultiplexCaseSlidesCasesParams}
            onClose={() => setIsWorkflowSpotDetectionOpen(false)}
            platformVersionWarning={platformVersionWarning}
          />
        )
      ) : null}
      {isWorkflowInitialImageAnalysisOpen ? (
        <DialogWithCloseButton
          header="Run initial image analysis"
          onClose={() => setIsWorkflowInitialImageAnalysisOpen(false)}
        >
          {renderWorkflowJobForm(
            <RunManualPostIngestPipeline
              casesParams={workflowInitialImageAnalysisCasesParams}
              study={study}
              onClose={() => setIsWorkflowInitialImageAnalysisOpen(false)}
              caseImageAnalysisStep={caseImageAnalysisStepControl}
            />
          )}
        </DialogWithCloseButton>
      ) : null}
      {isWorkflowCellSegmentationOpen ? (
        <DialogWithCloseButton
          header="Re-run cell segmentation"
          onClose={() => setIsWorkflowCellSegmentationOpen(false)}
        >
          {renderWorkflowJobForm(
            <RunMultiplexCellSegmentation
              casesParams={workflowMultiplexCaseSlidesCasesParams}
              onClose={() => setIsWorkflowCellSegmentationOpen(false)}
            />
          )}
        </DialogWithCloseButton>
      ) : null}
    </>
  );
};
